import { memo } from "react"
import { type PathParam, useParams } from "react-router"
import { useMutation } from "@tanstack/react-query"
import { AddRounded } from "@mui/icons-material"
import { IconButton } from "@mui/material"
import type { CollectionDocType } from "@oboku/shared"
import type { DeepReadonlyObject } from "rxdb"
import { NotFoundPage } from "../../../../common/NotFoundPage"
import { Page } from "../../../../common/Page"
import { TopBarNavigation } from "../../../../navigation/TopBarNavigation"
import {
  useAddCollectionToBook,
  useRemoveCollectionFromBook,
} from "../../../../books/helpers"
import { useBook } from "../../../../books/states"
import { useCollections } from "../../../../collections/useCollections"
import { SelectableCollectionList } from "../../../../collections/lists/SelectableCollectionList"
import { getCollectionComputedMetadata } from "../../../../collections/getCollectionComputedMetadata"
import { openAddCollectionDialog } from "../../../../library/shelves/AddCollectionDialog"
import { EntitySelectionView } from "../../../../common/selection"
import { notify } from "../../../../notifications/toasts"
import type { ROUTES } from "../../../../navigation/routes"

const getCollectionSearchableText = (
  collection: DeepReadonlyObject<CollectionDocType>,
) => getCollectionComputedMetadata(collection).title ?? ""

const EMPTY_COLLECTIONS: DeepReadonlyObject<CollectionDocType>[] = []
const listStyle = { flex: 1 }
const withoutGlobalErrorToast = { meta: { suppressGlobalErrorToast: true } }

export const BookCollectionsScreen = memo(function BookCollectionsScreen() {
  const { id: bookId } = useParams<PathParam<typeof ROUTES.BOOK_COLLECTIONS>>()
  const { data: collections = EMPTY_COLLECTIONS } = useCollections()
  const { data: book } = useBook({ id: bookId })
  const { mutateAsync: addCollectionToBook } = useAddCollectionToBook(
    withoutGlobalErrorToast,
  )
  const { mutateAsync: removeCollectionFromBook } = useRemoveCollectionFromBook(
    withoutGlobalErrorToast,
  )

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: async ({
      toAdd,
      toRemove,
    }: {
      toAdd: string[]
      toRemove: string[]
    }) => {
      if (!bookId) return
      await Promise.all([
        ...toAdd.map((collectionId) =>
          addCollectionToBook({ _id: bookId, collectionId }),
        ),
        ...toRemove.map((collectionId) =>
          removeCollectionFromBook({ _id: bookId, collectionId }),
        ),
      ])
    },
    onSuccess: () => {
      notify("actionSuccess")
    },
  })

  if (!bookId || book === null) return <NotFoundPage />

  return (
    <Page sx={{ overflow: "hidden" }} bottomGutter={false}>
      <TopBarNavigation title="Manage collections" showBack />
      <EntitySelectionView
        searchPlaceholder="Search collections…"
        searchAriaLabel="Search collections"
        items={collections}
        persistedIds={book?.collections}
        entityKey={bookId}
        getSearchableText={getCollectionSearchableText}
        isSaving={isSaving}
        onSave={save}
        toolbarActions={
          <IconButton
            onClick={openAddCollectionDialog}
            color="primary"
            disabled={isSaving}
            aria-label="Create a new collection"
          >
            <AddRounded />
          </IconButton>
        }
        renderList={({ filteredIds, selectedItems, toggleSelection }) => (
          <SelectableCollectionList
            style={listStyle}
            data={filteredIds}
            selected={selectedItems}
            onItemClick={({
              id: collectionId,
            }: {
              id: string
              selected: boolean
            }) => {
              toggleSelection(collectionId)
            }}
          />
        )}
      />
    </Page>
  )
})
